import { type ChecklistsPage, GraphQLChecklistsRequester, type NewChecklist, type UpdatedChecklist } from '@cobliteam/api-lib';
import type { DateRange, Sort } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { Alert } from '@cobliteam/ui/components';
import type { ChecklistStatus } from '@cobliteam/ui-business/components/MultiSelects/MultiSelectChecklistStatus/MultiSelectChecklistStatus';
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { type AllCompletedChecklists, COMPLETED_CHECKLISTS_EXPORT_PAGE_SIZE, collectAllCompletedChecklists } from './collectAllCompletedChecklists';

// Lazy instantiation to avoid circular dependency issues
const getGraphQLChecklistsRequester = () => new GraphQLChecklistsRequester();

export const QUERY_KEY = 'CHECKLIST';

const getTemplateChecklists = ({
  vehicleGroupIds,
  inputFilter,
  page,
  size,
  sort,
  vehicleIdsFilters,
  activeFilter,
  createdByFilter,
}: {
  vehicleGroupIds?: string[];
  inputFilter?: string;
  page?: number;
  size?: number;
  sort?: Sort;
  vehicleIdsFilters?: string[];
  activeFilter?: ChecklistStatus[];
  createdByFilter?: string[];
}) => {
  return useQuery<ChecklistsPage>({
    queryKey: [QUERY_KEY, 'template', vehicleGroupIds, inputFilter, page, size, sort, vehicleIdsFilters, activeFilter, createdByFilter],
    queryFn: async () => {
      const convertActiveFilter = (statusArray?: ChecklistStatus[]): boolean | undefined => {
        if (!statusArray || statusArray.length === 0) {
          return undefined;
        }
        if (statusArray.length === 2) {
          return undefined;
        }
        return statusArray[0] === 'active';
      };

      return getGraphQLChecklistsRequester().getTemplateChecklists(
        vehicleGroupIds,
        inputFilter,
        page,
        size,
        sort,
        vehicleIdsFilters,
        convertActiveFilter(activeFilter),
        createdByFilter
      );
    },
  });
};

export interface CompletedChecklistsParams {
  period?: DateRange;
  page?: number;
  size?: number;
  sort?: Sort;
  inputFilter?: string;
  nameFilters?: string[];
  vehicleGroupIds?: string[];
  vehicleIdsFilters?: string[];
  activeFilter?: boolean;
  createdByFilter?: string[];
  templateIdFilter?: string;
}

/** Filters of the completed-checklists listing without the pagination window — one page or every page. */
export type CompletedChecklistsFilters = Omit<CompletedChecklistsParams, 'page' | 'size'>;

const completedChecklistsQuery = ({
  period,
  page,
  size,
  sort,
  inputFilter,
  nameFilters,
  vehicleGroupIds,
  vehicleIdsFilters,
  activeFilter,
  createdByFilter,
  templateIdFilter,
}: CompletedChecklistsParams) => ({
  queryKey: [QUERY_KEY, 'completed', period, page, size, sort, inputFilter, nameFilters, vehicleGroupIds, vehicleIdsFilters, activeFilter, createdByFilter, templateIdFilter],
  queryFn: async () =>
    getGraphQLChecklistsRequester().getCompletedChecklists(
      period,
      page,
      size,
      sort,
      inputFilter,
      nameFilters,
      vehicleGroupIds,
      vehicleIdsFilters,
      activeFilter,
      createdByFilter,
      templateIdFilter
    ),
});

const getCompletedChecklists = (params: CompletedChecklistsParams, options?: Partial<UseQueryOptions<ChecklistsPage>>) => {
  return useQuery<ChecklistsPage>({ ...completedChecklistsQuery(params), ...options });
};

/**
 * Returns a callback that collects every completed checklist matching `filters`, page by page.
 * Imperative on purpose: the pages are only worth fetching when someone asks for an export, never as a
 * background query kept in sync with the table's filters.
 */
const useFetchAllCompletedChecklists = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (filters: CompletedChecklistsFilters): Promise<AllCompletedChecklists> =>
      collectAllCompletedChecklists((page) => queryClient.fetchQuery<ChecklistsPage>(completedChecklistsQuery({ ...filters, page, size: COMPLETED_CHECKLISTS_EXPORT_PAGE_SIZE }))),
    [queryClient]
  );
};

const getChecklistsNames = () => {
  return useQuery({
    queryKey: [QUERY_KEY, 'names'],
    queryFn: async () => {
      return await getGraphQLChecklistsRequester().getChecklistsNames(false);
    },
  });
};

const getChecklistById = (checklistId: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'byId', checklistId],
    queryFn: async () => {
      return await getGraphQLChecklistsRequester().getChecklistById(checklistId);
    },
    enabled: !!checklistId,
  });
};

const createChecklist = () => {
  const queryClient = useQueryClient();

  const invalidateChecklistDerivedQueries = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'template'] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'hasAny'] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'names'] });
  };

  return useMutation({
    mutationFn: async (createdChecklist: NewChecklist) => {
      return await getGraphQLChecklistsRequester().createChecklist(createdChecklist);
    },
    onSuccess: () => {
      invalidateChecklistDerivedQueries();

      Alert.success('Checklist criada com sucesso!');
    },
    onError: () => {
      Alert.error('Houve um erro, tente novamente mais tarde.');
    },
  });
};

const updateChecklist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updatedChecklist }: { id: string; updatedChecklist: UpdatedChecklist }) => {
      return await getGraphQLChecklistsRequester().updateChecklist(id, updatedChecklist);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'template'] });

      Alert.success('Checklist atualizada com sucesso!');
    },
    onError: (err) => {
      const errorMessage = err.message.includes('404: Not Found')
        ? 'Checklist não encontrada, verifique se ela já foi excluída ou atualize a página e tente novamente'
        : 'Houve um erro, tente novamente mais tarde.';
      Alert.error(errorMessage);
    },
  });
};

const deleteChecklist = () => {
  const queryClient = useQueryClient();

  const invalidateChecklistDerivedQueries = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'template'] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'hasAny'] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'names'] });
  };

  return useMutation({
    mutationFn: async (id: string) => {
      return await getGraphQLChecklistsRequester().deleteChecklist(id);
    },
    onSuccess: () => {
      invalidateChecklistDerivedQueries();

      Alert.success('Checklist excluída com sucesso!');
    },
    onError: () => {
      Alert.error('Houve um erro, tente novamente mais tarde.');
    },
  });
};

const toggleChecklistActive = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('checklists');

  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      return await getGraphQLChecklistsRequester().toggleChecklistActive(id, active);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'template'] });
      Alert.success(data.templateActive ? t('checklist_enabled_success') : t('checklist_disabled_success'));
    },
    onError: (_, variables) => {
      Alert.error(variables.active ? t('checklist_enabled_error') : t('checklist_disabled_error'));
    },
  });
};

const hasAnyChecklist = () => {
  return useQuery<boolean>({
    queryKey: [QUERY_KEY, 'hasAny'],
    queryFn: async () => {
      const result = await getGraphQLChecklistsRequester().getTemplateChecklists(undefined, undefined, 0, 1);
      return result.page.totalElements > 0;
    },
  });
};

const getTemplateChecklistsTotalCount = (options?: Partial<UseQueryOptions<number>>) => {
  return useQuery<number>({
    queryKey: [QUERY_KEY, 'totalCount'],
    queryFn: async () => {
      const result = await getGraphQLChecklistsRequester().getTemplateChecklists(undefined, undefined, 0, 1);
      return result.page.totalElements;
    },
    ...options,
  });
};

export {
  createChecklist,
  deleteChecklist,
  getChecklistById,
  getChecklistsNames,
  getCompletedChecklists,
  getTemplateChecklists,
  getTemplateChecklistsTotalCount,
  hasAnyChecklist,
  toggleChecklistActive,
  updateChecklist,
  useFetchAllCompletedChecklists,
};
